// Main Homebase Page - Routes to scenario-specific components
import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useHomebaseGenerate } from '../../hooks/useHomebaseGenerate';
import LoadingWithFactoids from '../../components/shared/LoadingWithFactoids';
import LayoutNoNav from '../../components/shared/LayoutNoNav';
import { trackEvent, mergeItineraryAttribution, saveItineraryAttribution } from '../../utils/analytics';

// Scenario Components
import BookedExactScenario from '../../components/homebase/BookedExactScenario';
import BookedAreaScenario from '../../components/homebase/BookedAreaScenario';
import BookedRegionScenario from '../../components/homebase/BookedRegionScenario';
import BookedSelectScenario from '../../components/homebase/BookedSelectScenario';
import RegionPrefScenario from '../../components/homebase/RegionPrefScenario';
import IslandWideScenario from '../../components/homebase/IslandWideScenario';
//

const HomeBaseViewer = () => {
  const { interviewId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, planningLoading, error, planningError, generateHomebase, ensurePlanningSyntheses } = useHomebaseGenerate();
  const [showOverlay, setShowOverlay] = React.useState(true);
  const handledRefreshRef = React.useRef(false);
  const analyticsTrackedRef = React.useRef(false);
  const planningState = React.useMemo(() => ({
    ready: Boolean(data?.planningSynthesesReady),
    loading: planningLoading,
    error: planningError,
    retry: interviewId ? () => ensurePlanningSyntheses(interviewId) : undefined
  }), [data?.planningSynthesesReady, planningLoading, planningError, ensurePlanningSyntheses, interviewId]);
  
  // Check if we need to refresh homebase data
  const shouldRefresh = searchParams.get('refresh') === 'true';

  // Single guarded effect to avoid double-calling during refresh clear and StrictMode
  useEffect(() => {
    if (!interviewId) return;

    if (shouldRefresh) {
      console.log('[HomeBaseViewer] Refresh requested, calling generateHomebase for:', interviewId);
      handledRefreshRef.current = true; // mark that we initiated via refresh
      void generateHomebase(interviewId).catch(() => undefined);
      // Clear the refresh parameter after calling to return to canonical URL
      const nextParams = new URLSearchParams(searchParams.toString());
      nextParams.delete('refresh');
      setSearchParams(nextParams);
      return;
    }

    // If we just cleared the refresh param, skip the immediate follow-up run
    if (handledRefreshRef.current) {
      handledRefreshRef.current = false;
      return;
    }

    console.log('[HomeBaseViewer] Calling generateHomebase for:', interviewId, {
      shouldRefresh,
      timestamp: new Date().toISOString()
    });
    void generateHomebase(interviewId).catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, shouldRefresh, generateHomebase]);

  React.useEffect(() => {
    analyticsTrackedRef.current = false;
  }, [interviewId]);

  React.useEffect(() => {
    if (!interviewId || !data || analyticsTrackedRef.current) return;
    const eventPayload = mergeItineraryAttribution({
      interview_id: interviewId,
      scenario: data?.scenario,
    });
    trackEvent('homebase_ready', eventPayload);
    saveItineraryAttribution(eventPayload);
    analyticsTrackedRef.current = true;
  }, [data, interviewId]);

  if (error) {
    return (
      <LayoutNoNav showHeader={true} headerExitRoute="/">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="glass-card rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-2">Error Loading Homebase</h2>
              <p className="text-white/70 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="btn-secondary"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </LayoutNoNav>
    );
  }

  // Keep showing the overlay until we've soft-exited at the end of a successful cycle.
  if (loading || showOverlay) {
    return (
      <LayoutNoNav showHeader={true} headerExitRoute="/">
        <LoadingWithFactoids
          shouldSoftExit={!loading && data}
          onSoftExitComplete={() => setShowOverlay(false)}
          progressMessages={[
            "Analyzing your interview answers...",
            "Matching areas to your travel style...",
            "This typically takes 20-30 seconds",
            "Personalizing your homebase recommendations...",
            "Finalizing your recommendations...",
            "Taking a bit longer than usual, almost there..."
          ]}
        />
      </LayoutNoNav>
    );
  }

  if (!data) {
    return (
      <LayoutNoNav showHeader={true} headerExitRoute="/">
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-white/70">No homebase data available.</p>
        </div>
      </LayoutNoNav>
    );
  }

  // Route to appropriate scenario component based on backend response
  const renderScenario = () => {
    const { scenario, data: scenarioData } = data;
    
    switch (scenario) {
      case 'booked-exact':
        return <BookedExactScenario data={scenarioData} interviewId={interviewId} planningState={planningState} />;

      case 'booked-high-confidence':
        return <BookedAreaScenario data={scenarioData} interviewId={interviewId} planningState={planningState} />;

      case 'booked-needs-area':
        return <BookedRegionScenario data={scenarioData} interviewId={interviewId} />;

      case 'booked-needs-region':
        return <BookedSelectScenario data={scenarioData} interviewId={interviewId} />;

      case 'not-booked-region-pref':
        return <RegionPrefScenario data={scenarioData} interviewId={interviewId} planningState={planningState} />;

      case 'not-booked-no-pref':
        return <IslandWideScenario data={scenarioData} interviewId={interviewId} planningState={planningState} />;
      case 'not-booked-area-known':
        return <BookedAreaScenario data={scenarioData} interviewId={interviewId} planningState={planningState} />;
      
      default:
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2 text-white">Unsupported Scenario</h2>
              <p className="text-white/70">Scenario: {scenario}</p>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>Homebase | Alaka'i Aloha</title>
      </Helmet>
      <LayoutNoNav showHeader={true} headerExitRoute="/">
        <div className="pt-4">
          {renderScenario()}
        </div>
      </LayoutNoNav>
    </>
  );
};

export default HomeBaseViewer;
